/**
 * bookingsSlice — source of truth for the Bookings list (Figma 5.1F).
 *
 * Wired to the live Bookings API (`events/api/v1/bookings/`):
 *  - `bookings` is hydrated from `getBookingsPageSSR` via `BookingsInitializer`.
 *  - Status-tab / search filtering AND pagination re-fetch server-side
 *    (`useBookings.fetchBookings`) and replace the list with one server page.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  type BookingEnumOptions,
  EMPTY_BOOKING_ENUMS,
} from "@/components/bookings/bookingEnums";
import type { BookingChangeRequest } from "@/components/bookings/detail/changeRequests";
import type { DetailPayment } from "@/components/bookings/detail/detailTabsData";
import type {
  BookingActivity,
  BookingNote,
} from "@/components/bookings/detail/notes";
import type { BookingProposal } from "@/components/bookings/detail/proposals";
import type { BookingDetail } from "@/components/bookings/detail/types";
import type { Booking } from "@/components/bookings/types";

/** Server pagination meta for the bookings list (from the API envelope). */
export interface BookingsPagination {
  count: number;
  totalPages: number;
  currentPage: number;
}

export interface BookingsState {
  bookings: Booking[];
  /** Server pagination meta — the table renders exactly one server page. */
  pagination: BookingsPagination;
  /** True until the list is hydrated (set false by BookingsInitializer). */
  loading: boolean;
  /** The open booking's detail (5.2F), hydrated by BookingDetailInitializer. */
  detail: BookingDetail | null;
  detailLoading: boolean;
  /** The open booking's notes + activity feed (5.2F), hydrated alongside detail. */
  notes: BookingNote[];
  notesLoading: boolean;
  activity: BookingActivity[];
  activityLoading: boolean;
  /** The open booking's change-request review queue (5.2F Proposals tab). */
  changeRequests: BookingChangeRequest[];
  /** The open booking's proposals/quotes (5.2F Proposals tab). */
  proposals: BookingProposal[];
  proposalsLoading: boolean;
  /** The open booking's payment log (5.2F Payment Log tab). */
  payments: DetailPayment[];
  /** True until the streamed payment-log read hydrates. */
  paymentsLoading: boolean;
  /** Backend-driven enum-dropdown option lists (type/status/payment/cancel). */
  enums: BookingEnumOptions;
}

const EMPTY_PAGINATION: BookingsPagination = {
  count: 0,
  totalPages: 1,
  currentPage: 1,
};

const initialState: BookingsState = {
  bookings: [],
  pagination: EMPTY_PAGINATION,
  loading: true,
  detail: null,
  detailLoading: false,
  // Detail-tab flags start true: the tabs stream in their own Suspense
  // boundaries, so first paint must show the skeleton — a `false` default
  // flashes the empty state before the post-paint reset flips them.
  notes: [],
  notesLoading: true,
  activity: [],
  activityLoading: true,
  changeRequests: [],
  proposals: [],
  proposalsLoading: true,
  payments: [],
  paymentsLoading: true,
  enums: EMPTY_BOOKING_ENUMS,
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    /** Replace the list with one server page (SSR hydration / filter re-fetch). */
    setBookings(
      state,
      action: PayloadAction<{
        rows: Booking[];
        count: number;
        totalPages: number;
        currentPage: number;
      }>,
    ) {
      state.bookings = action.payload.rows;
      state.pagination = {
        count: action.payload.count,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
      };
    },
    setBookingsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    /**
     * Reset the list to its loading state — dispatched when leaving the route so
     * a re-entry shows the loading skeleton instead of flashing the previous
     * visit's persisted (stale) list before the fresh SSR read re-hydrates. The
     * Redux store survives navigation; enums are kept (stable reference data,
     * re-hydrated on re-entry anyway).
     */
    resetBookingsList(state) {
      state.bookings = [];
      state.pagination = EMPTY_PAGINATION;
      state.loading = true;
    },
    /** Hydrate the open booking's detail (or null on a failed read). */
    setBookingDetail(state, action: PayloadAction<BookingDetail | null>) {
      state.detail = action.payload;
      state.detailLoading = false;
    },
    setBookingDetailLoading(state, action: PayloadAction<boolean>) {
      state.detailLoading = action.payload;
    },
    /** Clear streamed tab data and mark loaders pending (on detail hydrate / navigation). */
    resetBookingDetailSecondary(state) {
      state.notes = [];
      state.activity = [];
      state.changeRequests = [];
      state.proposals = [];
      state.payments = [];
      state.notesLoading = true;
      state.activityLoading = true;
      state.proposalsLoading = true;
      state.paymentsLoading = true;
    },
    /** Replace the open booking's notes (hydration + refetch after add/delete). */
    setBookingNotes(state, action: PayloadAction<BookingNote[]>) {
      state.notes = action.payload;
    },
    setBookingNotesLoading(state, action: PayloadAction<boolean>) {
      state.notesLoading = action.payload;
    },
    /** Replace the open booking's activity feed (hydration). */
    setBookingActivity(state, action: PayloadAction<BookingActivity[]>) {
      state.activity = action.payload;
    },
    setBookingActivityLoading(state, action: PayloadAction<boolean>) {
      state.activityLoading = action.payload;
    },
    /** Replace the open booking's change requests (hydration + refetch after review). */
    setBookingChangeRequests(
      state,
      action: PayloadAction<BookingChangeRequest[]>,
    ) {
      state.changeRequests = action.payload;
    },
    /** Replace the open booking's proposals (hydration + refetch after withdraw). */
    setBookingProposals(state, action: PayloadAction<BookingProposal[]>) {
      state.proposals = action.payload;
    },
    setBookingProposalsLoading(state, action: PayloadAction<boolean>) {
      state.proposalsLoading = action.payload;
    },
    /** Replace the open booking's payment log (hydration + refresh after record). */
    setBookingPayments(state, action: PayloadAction<DetailPayment[]>) {
      state.payments = action.payload;
    },
    setBookingPaymentsLoading(state, action: PayloadAction<boolean>) {
      state.paymentsLoading = action.payload;
    },
    /** Replace the backend-driven enum-dropdown option lists (hydration). */
    setBookingEnums(state, action: PayloadAction<BookingEnumOptions>) {
      state.enums = action.payload;
    },
  },
});

export const {
  setBookings,
  setBookingsLoading,
  resetBookingsList,
  setBookingDetail,
  setBookingDetailLoading,
  resetBookingDetailSecondary,
  setBookingNotes,
  setBookingNotesLoading,
  setBookingActivity,
  setBookingActivityLoading,
  setBookingChangeRequests,
  setBookingProposals,
  setBookingProposalsLoading,
  setBookingPayments,
  setBookingPaymentsLoading,
  setBookingEnums,
} = bookingsSlice.actions;

export default bookingsSlice.reducer;
